import { useMemo } from 'react';
import { AreaChart, BarChart, Card, Text, Title } from '@tremor/react';
import type { SalesTicket } from '../api/types';
import { formatMxn } from '../lib/format';
import { t } from '../lib/i18n';
import IconButton from './IconButton';
import Modal from './Modal';
import { CloseIcon } from './icons';

interface Props {
  /** Tickets already filtered to the same window the parent modal uses. */
  tickets: SalesTicket[];
  /** Line clicked in the Por-línea chart upstream. */
  line: string;
  onClose: () => void;
}

const TOP_N = 20;

/**
 * Drill-down into one partf.line: which 20 parts within it generated the most
 * revenue, and how does their daily revenue compare to the rest of the line's
 * parts over the same window. Pure client-side aggregation over the tickets
 * the parent already has — no new daemon call.
 */
export default function LinePartsDrilldownModal({ tickets, line, onClose }: Props) {
  // Bucket every titm in this line by part; sum extended revenue.
  const partTotals = useMemo(() => {
    const byPart = new Map<string, number>();
    for (const r of tickets) {
      for (const li of r.lines) {
        if (li.line !== line) continue;
        const ext = li.sell_mxn * li.qty_sold;
        byPart.set(li.part, (byPart.get(li.part) ?? 0) + ext);
      }
    }
    return [...byPart]
      .map(([part, value]) => ({ part, value }))
      .filter((s) => s.value !== 0)
      .sort((a, b) => b.value - a.value);
  }, [tickets, line]);

  const top20 = partTotals.slice(0, TOP_N);
  const top20Set = useMemo(() => new Set(top20.map((p) => p.part)), [top20]);
  const lineTotal = partTotals.reduce((a, p) => a + p.value, 0);
  const top20Total = top20.reduce((a, p) => a + p.value, 0);

  // Daily series: for each idate in the window, sum revenue split into
  //   "Top 20" — lines whose part is in the top-20 set
  //   "Resto"  — every other part still belonging to this line
  const trend = useMemo(() => {
    const byDay = new Map<string, { top: number; rest: number }>();
    for (const r of tickets) {
      for (const li of r.lines) {
        if (li.line !== line) continue;
        const ext = li.sell_mxn * li.qty_sold;
        const day = r.idate;
        const bucket = byDay.get(day) ?? { top: 0, rest: 0 };
        if (top20Set.has(li.part)) bucket.top += ext;
        else                       bucket.rest += ext;
        byDay.set(day, bucket);
      }
    }
    const out = [...byDay]
      .map(([day, v]) => ({ day, [t.lineDrill.trend.top20]: v.top, [t.lineDrill.trend.rest]: v.rest }))
      .sort((a, b) => (a.day < b.day ? -1 : 1));
    return out;
  }, [tickets, line, top20Set]);

  const barData = top20.map((p) => ({ name: p.part, [t.series.revenue]: p.value }));

  return (
    <Modal
      onClose={onClose}
      ariaLabel={t.lineDrill.title.replace('{line}', line)}
      role="dialog"
      panelClassName="max-w-5xl p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <div>
          <Title>{t.lineDrill.title.replace('{line}', line)}</Title>
          <Text className="text-tremor-content text-sm">
            {t.lineDrill.subtitle
              .replace('{n}', String(top20.length))
              .replace('{total}', formatMxn(lineTotal))
              .replace('{topTotal}', formatMxn(top20Total))
              .replace('{pct}', lineTotal > 0 ? Math.round(100 * top20Total / lineTotal) + '%' : '—')}
          </Text>
        </div>
        <IconButton label={t.common.close} onClick={onClose} className="ml-auto">
          <CloseIcon />
        </IconButton>
      </div>

      {top20.length === 0 ? (
        <Text className="text-tremor-content">{t.lineDrill.empty.replace('{line}', line)}</Text>
      ) : (
        <>
          <Card>
            <Title className="text-base">{t.lineDrill.top20Title}</Title>
            <BarChart
              className="mt-3 h-72"
              data={barData}
              index="name"
              categories={[t.series.revenue]}
              colors={['blue']}
              valueFormatter={formatMxn}
              yAxisWidth={80}
              layout="vertical"
            />
          </Card>

          <Card>
            <Title className="text-base">{t.lineDrill.trendTitle}</Title>
            {trend.length < 2 ? (
              <Text className="text-tremor-content text-sm mt-2">{t.lineDrill.trendTooShort}</Text>
            ) : (
              <AreaChart
                className="mt-3 h-64"
                data={trend}
                index="day"
                categories={[t.lineDrill.trend.top20, t.lineDrill.trend.rest]}
                colors={['blue', 'gray']}
                valueFormatter={formatMxn}
                yAxisWidth={72}
                stack
              />
            )}
          </Card>
        </>
      )}
    </Modal>
  );
}
